import { Directive, ElementRef, EmbeddedViewRef, HostListener, Input, OnInit, Renderer2, SimpleChanges, OnChanges } from '@angular/core';
import { PopoverArrowPosition, PopoverContainerPosition } from '../domain/popover-position.enum';
import { LiveAnnouncer } from '@angular/cdk/a11y';

interface Config {
  containerPosition: PopoverContainerPosition;
  arrowPosition: PopoverArrowPosition;
  width?: string;
  viewportTopOffset: number;
  viewportHeight: number;
  ariaLabel: string;
}

@Directive({
  selector: '[ftsCustomPopover]',
})
export class CustomPopoverDirective implements OnInit, OnChanges {
  @Input()
  ftsCustomPopover: ElementRef;
  @Input()
  popoverContextEl: any;
  @Input()
  config: Config;
  @Input()
  show = false;
  @Input()
  popoverManualOffset: number;
  @Input()
  isSecondHalf = false;

  private arrowEl: any;
  private arrowShadowEl: any;

  private contextEl: any;
  private popoverOpen: boolean;
  private view: any;
  private SPACE_KEY_CODE = 32;
  private SPACE_KEY = ' ';

  constructor(private host: ElementRef, private renderer: Renderer2, private liveAnnouncer: LiveAnnouncer) {}

  ngOnInit() {
    this.updateContextElAndSetPosition();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['show'] && !changes['show'].firstChange) {
      if (this.show) {
        this.showPopover();
      } else {
        this.hidePopover();
      }
    }

    if (changes['popoverContextEl']) {
      this.updateContextElAndSetPosition();
    }
  }

  @HostListener('click')
  onClick() {
    if (this.popoverOpen) {
      this.hidePopover();
    } else {
      this.showPopover();
    }
  }

  @HostListener('keypress', ['$event'])
  onKeyPress(event) {
    if (
      (event.key !== undefined && event.key === this.SPACE_KEY) ||
      (event.keyCode !== undefined && event.keyCode === this.SPACE_KEY_CODE)
    ) {
      this.onClick();
    }
  }

  @HostListener('document:keydown.escape')
  onDocumentKeyPress() {
    if (this.popoverOpen) {
      this.hidePopover();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event) {
    if (this.popoverOpen && !this.host.nativeElement.contains(event.target) && this.view && !this.view.contains(event.target)) {
      this.hidePopover();
    }
  }

  private updateContextElAndSetPosition() {
    this.contextEl = this.popoverContextEl.nativeElement || this.host.nativeElement;
    if (!!this.contextEl) {
      this.renderer.setStyle(this.contextEl, 'position', 'relative');
    }
  }

  private hidePopover() {
    this.liveAnnouncer.announce(`Popover menu ${this.config.ariaLabel} closed`, 2000);
    this.popoverOpen = false;
    this.renderer.setStyle(this.view, 'display', 'none');
    this.renderer.setStyle(this.view, 'top', '');
    this.renderer.setStyle(this.view, 'bottom', '');
    this.renderer.removeChild(this.view, this.arrowEl);
    this.renderer.removeChild(this.view, this.arrowShadowEl);
    this.arrowEl = null;
    this.arrowShadowEl = null;
  }

  private showPopover() {
    this.liveAnnouncer.announce(`Popover menu ${this.config.ariaLabel} opened`, 2000);
    this.popoverOpen = true;
    this.view = this.ftsCustomPopover.nativeElement;
    this.renderer.addClass(this.view, 'popover-container');
    this.renderer.setStyle(this.view, 'display', 'flex');

    if (this.config && this.config.width) {
      this.renderer.setStyle(this.view, 'width', this.config.width);
    } else {
      this.renderer.setStyle(this.view, 'max-width', '100%');
    }

    let containerPosition = (this.config && this.config.containerPosition) || PopoverContainerPosition.Top;
    if (containerPosition === PopoverContainerPosition.Top) {
      containerPosition = this.doesPopoverFitAbove(this.view) ? containerPosition : PopoverContainerPosition.Bottom;
    } else if (containerPosition === PopoverContainerPosition.Bottom) {
      containerPosition = this.doesPopoverFitBelow(this.view) ? containerPosition : PopoverContainerPosition.Top;
    }
    this.renderConainer(this.view, containerPosition);
    this.renderArrow(this.view, containerPosition);
  }

  private renderConainer(view: EmbeddedViewRef<any>, containerPosition: PopoverContainerPosition) {
    if (containerPosition === PopoverContainerPosition.Top) {
      this.renderer.setStyle(
        view,
        'bottom',
        this.popoverManualOffset ? `${this.isSecondHalf ? this.popoverManualOffset - 50 : this.popoverManualOffset}%` : '130%'
      );
    } else if (containerPosition === PopoverContainerPosition.Bottom) {
      this.renderer.setStyle(
        view,
        'top',
        this.popoverManualOffset ? `${this.isSecondHalf ? this.popoverManualOffset : this.popoverManualOffset - 50}%` : '130%'
      );
    }
  }

  private renderArrow(view: EmbeddedViewRef<any>, containerPosition: PopoverContainerPosition) {
    const arrowPosition = this.config.arrowPosition;
    this.arrowEl = this.renderer.createElement('div');
    this.arrowShadowEl = this.renderer.createElement('div');

    this.renderer.addClass(this.arrowEl, 'primary-bg-border-color');
    this.renderer.addClass(this.arrowEl, 'arrow');
    this.renderer.addClass(this.arrowEl, `arrow-${containerPosition.toLowerCase()}`);

    this.renderer.addClass(this.arrowShadowEl, 'arrow-shadow');
    this.renderer.addClass(this.arrowShadowEl, `arrow-${containerPosition.toLowerCase()}`);

    switch (arrowPosition) {
      case PopoverArrowPosition.Right:
        this.renderer.setStyle(this.arrowEl, 'right', `${this.getArrowOffset()}px`);
        this.renderer.setStyle(this.arrowShadowEl, 'right', `${this.getArrowShadowOffset()}px`);
        break;
      case PopoverArrowPosition.Left:
        this.renderer.setStyle(this.arrowEl, 'left', `${this.getArrowOffset()}px`);
        this.renderer.setStyle(this.arrowShadowEl, 'left', `${this.getArrowShadowOffset()}px`);
        break;
      default:
        this.renderer.addClass(this.arrowEl, 'arrow-center');
        this.renderer.addClass(this.arrowShadowEl, 'arrow-center');
        break;
    }

    this.renderer.appendChild(view, this.arrowEl);
    this.renderer.appendChild(view, this.arrowShadowEl);
  }

  private getArrowOffset(): number {
    const arrowBorderWidth = 11;
    const hostHalfWidth = this.host.nativeElement.getBoundingClientRect().width / 2;
    return hostHalfWidth - arrowBorderWidth;
  }

  private getArrowShadowOffset(): number {
    const arrowShadowBorderWidth = 15;
    const hostHalfWidth = this.host.nativeElement.getBoundingClientRect().width / 2;
    return hostHalfWidth - arrowShadowBorderWidth;
  }

  private doesPopoverFitAbove(view: any): boolean {
    const allocatedHeight = this.getAllocatedHeight();
    const popoverContainerHeight = view.getBoundingClientRect().height;

    return popoverContainerHeight < allocatedHeight;
  }

  private doesPopoverFitBelow(view: any): boolean {
    const allocatedHeight = this.getAllocatedHeight();
    const popoverContainerHeight = view.getBoundingClientRect().height;

    return popoverContainerHeight < allocatedHeight;
  }

  private getAllocatedHeight(): number {
    const hostTop = this.host.nativeElement.getBoundingClientRect().top;
    const hostHeight = this.host.nativeElement.getBoundingClientRect().height;

    if (this.config.containerPosition === PopoverContainerPosition.Top) {
      return hostTop - this.config.viewportTopOffset;
    } else if (this.config.containerPosition === PopoverContainerPosition.Bottom) {
      return this.config.viewportHeight - (hostTop + hostHeight);
    }
  }
}
